import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || '';
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';

// Create Supabase client
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: false
  }
});

export type Json =
  | string
  | number
  | boolean
  | null
  | { [key: string]: Json | undefined }
  | Json[]

export interface Database {
  public: {
    Tables: {
      marketing_testimonials: {
        Row: {
          id: string
          customer_name: string
          business_name: string
          business_type: string
          plan_tier: 'Free' | 'Premium' | 'Professional'
          monthly_revenue: string
          testimonial_quote: string
          rating: number
          avatar_url: string | null
          location: string | null
          years_in_business: string | null
          industry_category: string | null
          featured: boolean
          display_order: number
          created_at: string
          updated_at: string
        }
        Insert: {
          id?: string
          customer_name: string
          business_name: string
          business_type: string
          plan_tier: 'Free' | 'Premium' | 'Professional'
          monthly_revenue: string
          testimonial_quote: string
          rating: number
          avatar_url?: string | null
          location?: string | null
          years_in_business?: string | null
          industry_category?: string | null
          featured?: boolean
          display_order?: number
          created_at?: string
          updated_at?: string
        }
        Update: {
          id?: string
          customer_name?: string
          business_name?: string
          business_type?: string
          plan_tier?: 'Free' | 'Premium' | 'Professional'
          monthly_revenue?: string
          testimonial_quote?: string
          rating?: number
          avatar_url?: string | null
          location?: string | null
          years_in_business?: string | null
          industry_category?: string | null
          featured?: boolean
          display_order?: number
          created_at?: string
          updated_at?: string
        }
      }
      subscriptions: {
        Row: {
          id: string
          user_id: string
          title: string
          category: string
          subcategory: string
          description: string
          price: number
          custom_url: string
          is_active: boolean
          created_at: string
          updated_at: string
          stripe_product_id: string | null
          stripe_price_id: string | null
          creator_name: string | null
          creator_email: string
          stripe_connect_account_id: string | null
          custom_features: string[] | null
          email_display: boolean | null
          phone_display: boolean | null
          service_address: string | null
          service_city: string | null
          service_state: string | null
          service_zip_code: string | null
          service_radius_miles: number | null
          latitude: number | null
          longitude: number | null
          platform_stripe_product_id: string | null
          platform_stripe_price_id: string | null
        }
        Insert: {
          id?: string
          user_id: string
          title: string
          category: string
          subcategory: string
          description: string
          price: number
          custom_url: string
          is_active?: boolean
          created_at?: string
          updated_at?: string
          stripe_product_id?: string | null
          stripe_price_id?: string | null
          creator_name?: string | null
          creator_email: string
          stripe_connect_account_id?: string | null
          custom_features?: string[] | null
          email_display?: boolean | null
          phone_display?: boolean | null
          service_address?: string | null
          service_city?: string | null
          service_state?: string | null
          service_zip_code?: string | null
          service_radius_miles?: number | null
          latitude?: number | null
          longitude?: number | null
          platform_stripe_product_id?: string | null
          platform_stripe_price_id?: string | null
        }
        Update: {
          id?: string
          user_id?: string
          title?: string
          category?: string
          subcategory?: string
          description?: string
          price?: number
          custom_url?: string
          is_active?: boolean
          created_at?: string
          updated_at?: string
          stripe_product_id?: string | null
          stripe_price_id?: string | null
          creator_name?: string | null
          creator_email?: string
          stripe_connect_account_id?: string | null
          custom_features?: string[] | null
          email_display?: boolean | null
          phone_display?: boolean | null
          service_address?: string | null
          service_city?: string | null
          service_state?: string | null
          service_zip_code?: string | null
          service_radius_miles?: number | null
          latitude?: number | null
          longitude?: number | null
          platform_stripe_product_id?: string | null
          platform_stripe_price_id?: string | null
        }
      }
      subscribers: {
        Row: {
          id: string
          subscription_id: string
          email: string
          name: string
          created_at: string
          payment_status: string
          stripe_customer_id: string | null
          stripe_subscription_id: string | null
          current_period_start: string | null
          current_period_end: string | null
        }
        Insert: {
          id?: string
          subscription_id: string
          email: string
          name: string
          created_at?: string
          payment_status?: string
          stripe_customer_id?: string | null
          stripe_subscription_id?: string | null
          current_period_start?: string | null
          current_period_end?: string | null
        }
        Update: {
          id?: string
          subscription_id?: string
          email?: string
          name?: string
          created_at?: string
          payment_status?: string
          stripe_customer_id?: string | null
          stripe_subscription_id?: string | null
          current_period_start?: string | null
          current_period_end?: string | null
        }
      }
      user_profiles: {
        Row: {
          id: string
          user_id: string
          subscription_tier: string
          business_name: string | null
          business_type: string | null
          onboarding_completed: boolean
          created_at: string
          updated_at: string
          full_name: string | null
          email: string | null
          stripe_platform_customer_id: string | null
          phone_number: string | null
        }
        Insert: {
          id?: string
          user_id: string
          subscription_tier?: string
          business_name?: string | null
          business_type?: string | null
          onboarding_completed?: boolean
          created_at?: string
          updated_at?: string
          full_name?: string | null
          email?: string | null
          stripe_platform_customer_id?: string | null
          phone_number?: string | null
        }
        Update: {
          id?: string
          user_id?: string
          subscription_tier?: string
          business_name?: string | null
          business_type?: string | null
          onboarding_completed?: boolean
          created_at?: string
          updated_at?: string
          full_name?: string | null
          email?: string | null
          stripe_platform_customer_id?: string | null
          phone_number?: string | null
        }
      }
    }
    Views: {
      [_ in never]: never
    }
    Functions: {
      [_ in never]: never
    }
    Enums: {
      [_ in never]: never
    }
  }
}